import { useContext, useEffect, useState } from "react";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import Button from "../components/UI/Button";
import Loader from "../components/UI/Loader";
import { OrganizationContext } from "../App";
import { format } from "date-fns";
import { getDemographicsGenderMode, getDemographicsRaceMode, getVisibleDemographicsFields } from "../utils/demographicsSchema";
import { configuredRequiredFieldsOrgSettingsEnabled, demographicsOrgSettingsEnabled, householdMemberDemographicsReportsEnabled } from "../utils/features";
import { ConfiguredRequiredField, getOrganizationConfiguredRequiredFields } from "../services/organizationService";
import { formatJSONDateWithoutTime } from "../utils/dateHelpers";

const getDateOfBirthEffectiveDateDisplay = (dateOfBirthField: ConfiguredRequiredField | undefined) => {
    if (!dateOfBirthField) {
        return 'Not set';
    }

    if (!dateOfBirthField.effectiveDate) {
        return 'No effective date set; applies to all records now';
    }

    return formatJSONDateWithoutTime(dateOfBirthField.effectiveDate, 'MM/dd/yyyy', true);
};

const ViewOrganization = () => {
    const organization = useContext(OrganizationContext);
    const visibleFields = getVisibleDemographicsFields(organization.value);
    const [dateOfBirthField, setDateOfBirthField] = useState<ConfiguredRequiredField | undefined>();
    const [requiredFieldsLoaded, setRequiredFieldsLoaded] = useState(false);

    useEffect(() => {
        document.title = "Manage Organization"
    }, []);

    useEffect(() => {
        if (!configuredRequiredFieldsOrgSettingsEnabled || organization.loading || !organization.value) {
            return;
        }

        setRequiredFieldsLoaded(false);
        getOrganizationConfiguredRequiredFields().then(requiredFields => {
            setDateOfBirthField(requiredFields.find(field => field.fieldName === 'dateOfBirth'));
        }).catch(error => {
            console.error(error);
        }).finally(() => {
            setRequiredFieldsLoaded(true);
        });
    }, [organization.loading, organization.value]);

    return (<section id="main-content" tabIndex={-1}>
        {organization.loading && <Loader />}
        {!organization.loading && organization.value && <>
            <div className="row justify-content-between align-items-center">
                <div className="column-auto">
                    <h1>Manage Organization</h1>
                </div>
                <div className="column-auto">
                    <Button id="edit-organization-button" className="secondary" text="Edit" href="/admin/organization/edit" />
                </div>
            </div>
            {organization.value?.planEndDate && <div className="row">
                <div className="column-auto">
                    Plan End Date: {format(new Date(organization.value!.planEndDate), 'MM/dd/yyyy')}
                </div>
            </div>}
            <div className="row justify-content-center margin-vertical-2">
                <div className="column-medium">
                    <ApiErrorMessages error={organization.error} />
                    {organization && <>
                        <div className="row margin-bottom-4">
                            <div className="column">
                                <h2>Organization Contact Information</h2>
                                <div className="row margin-vertical-4">
                                    <div className="column margin-top-5">
                                        <label>Name</label>
                                        <div>{organization.value.name}</div>
                                    </div>
                                </div>
                                {organization.value.url && <div className="row margin-vertical-4">
                                    <div className="column margin-top-5">
                                        <label>URL</label>
                                        <div>{organization.value.url}</div>
                                    </div>
                                </div>}
                                <div className="row margin-vertical-4">
                                    <div className="column margin-top-5">
                                        <label>Phone Number</label>
                                        <div>{organization.value.phoneNumber}</div>
                                    </div>
                                    <div className="column margin-top-5">
                                        <label>Email</label>
                                        <div>{organization.value.email}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="column">
                                <h2>Address</h2>
                                <div className="row margin-vertical-4">
                                    <div className="column margin-top-5">
                                        <label>Address</label>
                                        <div>{organization.value.addressLine1}</div>
                                        {organization.value.addressLine2 && <div>{organization.value.addressLine2}</div>}
                                        <div>{organization.value.city}, {organization.value.state} {organization.value.zipCode}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="column">
                                <h2>Data Preferences</h2>
                                {configuredRequiredFieldsOrgSettingsEnabled && <div className="row margin-vertical-4">
                                    <div className="column margin-top-5">
                                        <label>Date of Birth Required</label>
                                        <div>{requiredFieldsLoaded ? (dateOfBirthField ? 'Yes' : 'No') : 'Loading...'}</div>
                                    </div>
                                    <div className="column margin-top-5">
                                        <label>Date of Birth Effective Date</label>
                                        <div>{requiredFieldsLoaded ? getDateOfBirthEffectiveDateDisplay(dateOfBirthField) : 'Loading...'}</div>
                                    </div>
                                </div>}
                                {householdMemberDemographicsReportsEnabled && <div className="row margin-vertical-4">
                                    <div className="column margin-top-5">
                                        <label>New Household Member Default</label>
                                        <div>{{
                                            defaultUnchecked: 'Default to not a full participant',
                                            defaultChecked: 'Default to a full participant',
                                            alwaysHouseholdMember: 'Always keep as a household member',
                                            alwaysFullParticipant: 'Always make a full participant'
                                        }[organization.value.householdMemberParticipantMode || 'defaultUnchecked']}</div>
                                    </div>
                                </div>}
                                {demographicsOrgSettingsEnabled && <>
                                    <div className="row margin-vertical-4">
                                        <div className="column margin-top-5">
                                            <label>Demographics Visibility</label>
                                            <div>{visibleFields.join(', ')}</div>
                                        </div>
                                    </div>
                                    <div className="row margin-vertical-4">
                                        <div className="column margin-top-5">
                                            <label>Gender Options</label>
                                            <div>{getDemographicsGenderMode(organization.value) === 'simplified' ? 'Simplified: Male, Female, Other' : 'Default Options'}</div>
                                        </div>
                                        <div className="column margin-top-5">
                                            <label>Race and Ethnicity Options</label>
                                            <div>{getDemographicsRaceMode(organization.value) === 'combined' ? 'OMB-Aligned: Combined Race/Ethnicity question' : 'Default: Separate Race and Ethnicity questions'}</div>
                                        </div>
                                    </div>
                                </>}
                            </div>
                        </div>
                    </>}
                </div>
            </div>
        </>}
    </section>
    )
};

export default ViewOrganization;
